import {
  Menu,
  Popover,
  Transition,
  PopoverPanel,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, useState } from 'react';
import tw, { styled } from 'twin.macro';
import { Duration } from 'date-fns';

import DateRangePopup from '~/shared/components/DateRangePopup';
import ChevronIcon from '~/shared/assets/icons/chevron.svg';
import CalendarIcon from '~/shared/assets/icons/calendar.svg';
import { getDateFilterDates } from '~/shared/utils/getDateFilterDates';
import { formatDate } from '~/shared/utils/formatNumber';

import { CUSTOM_DATES_TITLE } from '../common/dateFilterMenuItems';

const ItemsContainer = tw(MenuItems)`
   absolute right-0 top-[100%] mt-[4px] origin-top-right font-light
   rounded-md w-[268px] bg-white
   shadow-dropdown focus:outline-none z-50 
 ` as typeof MenuItems;

const MenuBtn = styled.button<{
  $active?: boolean;
}>(({ $active }) => [
  tw`text-[16px] leading-6 mb-1 text-black
  w-full h-[48px] flex items-center justify-between rounded-md px-2 py-[2px] hover:bg-primary-50`,
  $active ? tw`bg-primary-100 text-primary-500 hover:bg-primary-100` : '',
]);

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

function ChartFilterDropdown<
  T extends { title: string; duration?: { start: Duration; end?: Duration } },
>({
  items,
  onSelect,
  selectedItem,
}: {
  onSelect: (selection: T) => void;
  items?: T[];
  selectedItem: T;
}) {
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);

  const isCustomDateSelected = selectedItem.title?.includes(CUSTOM_DATES_TITLE);

  const [startDate, endDate] = getDateFilterDates(selectedItem?.duration, selectedItem.title);

  const handleCustomDateSelect = (startDate: Date, endDate: Date) => {
    const start = startDate.toISOString();
    const end = endDate.toISOString();
    const customItem = {
      title: `${CUSTOM_DATES_TITLE}|${start}|${end}`,
    };

    onSelect(customItem as T);
    setIsCustomDateOpen(false);
  };

  return (
    <Menu as="div" className="relative flex">
      <MenuButton
        className="bg-white text-primary-900 border border-primary-500 flex justify-between min-w-[203px] rounded-[8px] px-[16px] py-[12px]"
        onClick={() => {
          if (isCustomDateOpen) {
            setIsCustomDateOpen(false);
          }
        }}
      >
        {({ open }) => (
          <>
            <div className="font-normal text-[16px] leading-[20px] text-[#544A4A]">
              {isCustomDateSelected
                ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                : selectedItem?.title}
            </div>

            <ChevronIcon
              className={clsx('fill-[#1D1D1D]', open ? 'rotate-180' : '')}
              width={16}
              height={16}
            />
          </>
        )}
      </MenuButton>

      <Transition as={Fragment} {...transitionProps}>
        <ItemsContainer>
          <div className="p-3">
            {items?.map((item) => (
              <MenuItem key={item.title}>
                <MenuBtn
                  onClick={() => {
                    if (item.title === CUSTOM_DATES_TITLE) {
                      setIsCustomDateOpen(true);
                    } else {
                      onSelect?.(item);
                    }
                  }}
                  $active={item.title === selectedItem?.title}
                >
                  {item.title}
                  {item.title === 'Custom dates' && (
                    <CalendarIcon
                      width="24px"
                      className={clsx(
                        item.title === selectedItem?.title ? 'stroke-[#2151F5]' : 'stroke-[#2D2D32]'
                      )}
                    />
                  )}
                </MenuBtn>
              </MenuItem>
            ))}
          </div>
        </ItemsContainer>
      </Transition>

      <Popover>
        <PopoverPanel
          static
          className={clsx(
            'absolute z-10 top-[100%] mt-[4px] right-0 shadow-dropdown rounded-md w-fit px-4 sm:px-0 origin-top-right',
            isCustomDateOpen ? 'block' : 'hidden'
          )}
        >
          {isCustomDateOpen && (
            <DateRangePopup
              startDateStr={startDate}
              endDateStr={endDate}
              onSubmit={handleCustomDateSelect}
              onClose={() => setIsCustomDateOpen(false)}
            />
          )}
        </PopoverPanel>
      </Popover>
    </Menu>
  );
}

export default ChartFilterDropdown;
